// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { HubContext } from 'components/common/emitter/HubContext';
import { ProjectExportFailedMessage, ProjectExportProgressMessage } from 'model/reponse/ProjectExportProgressMessage';

const PACKAGING_PROGRESS_MAX = 80;
const TRANSFER_PROGRESS_MAX = 99;

export type ProjectExportPhase = 'preparing' | 'packing' | 'transferring';

type ActiveExport = {
  projectId: string;
  controller: AbortController;
  cancelled: boolean;
};

export type ExportStatusContextValue = {
  exportingProjectId?: string;
  isExporting: boolean;
  progress: number;
  phase: ProjectExportPhase;
  startExport: (projectId: string) => AbortSignal | undefined;
  wasExportCancelled: () => boolean;
  finishExport: () => void;
  cancelExport: () => void;
  updateTransferProgress: (progress?: number) => void;
};

const ExportStatusContext = createContext<ExportStatusContextValue | undefined>(undefined);

interface Props {
  children: React.ReactNode;
}

export function ExportStatusProvider({ children }: Readonly<Props>) {
  const [exportingProjectId, setExportingProjectId] = useState<string | undefined>();
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState<ProjectExportPhase>('preparing');
  const activeExportRef = useRef<ActiveExport | undefined>(undefined);
  const { events } = useContext(HubContext);

  const startExport = useCallback((projectId: string) => {
    if (activeExportRef.current) return undefined;

    const controller = new AbortController();
    activeExportRef.current = { projectId, controller, cancelled: false };
    setExportingProjectId(projectId);
    setProgress(0);
    setPhase('preparing');
    return controller.signal;
  }, []);

  const wasExportCancelled = useCallback(() => activeExportRef.current?.cancelled ?? false, []);

  const finishExport = useCallback(() => {
    activeExportRef.current = undefined;
    setExportingProjectId(undefined);
    setProgress(0);
    setPhase('preparing');
  }, []);

  const cancelExport = useCallback(() => {
    const activeExport = activeExportRef.current;
    if (!activeExport) return;
    activeExport.cancelled = true;
    activeExport.controller.abort();
  }, []);

  const updateTransferProgress = useCallback((transferProgress?: number) => {
    if (!activeExportRef.current || activeExportRef.current.cancelled) return;
    setPhase('transferring');
    setProgress(currentProgress => {
      if (transferProgress === undefined) return Math.max(currentProgress, PACKAGING_PROGRESS_MAX);

      const normalizedProgress = Math.min(1, Math.max(0, transferProgress));
      const mappedProgress =
        PACKAGING_PROGRESS_MAX + normalizedProgress * (TRANSFER_PROGRESS_MAX - PACKAGING_PROGRESS_MAX);
      return Math.max(currentProgress, mappedProgress);
    });
  }, []);

  useEffect(() => {
    function onProgress(msg: ProjectExportProgressMessage) {
      const activeExport = activeExportRef.current;
      if (msg.projectId !== activeExport?.projectId || activeExport.cancelled) return;
      const normalizedProgress = Math.min(1, Math.max(0, msg.progress));
      if (normalizedProgress > 0) {
        setPhase(currentPhase => (currentPhase === 'preparing' ? 'packing' : currentPhase));
      }
      setProgress(currentProgress => Math.max(currentProgress, normalizedProgress * PACKAGING_PROGRESS_MAX));
    }
    function onFailed(msg: ProjectExportFailedMessage) {
      const activeExport = activeExportRef.current;
      if (msg.projectId !== activeExport?.projectId || activeExport.cancelled) return;
      activeExport.controller.abort();
    }
    events.on('onProjectExportProgress', onProgress);
    events.on('onProjectExportFailed', onFailed);
    return () => {
      events.off('onProjectExportProgress', onProgress);
      events.off('onProjectExportFailed', onFailed);
    };
  }, [events]);

  const value = useMemo(
    () => ({
      exportingProjectId,
      isExporting: Boolean(exportingProjectId),
      progress,
      phase,
      startExport,
      wasExportCancelled,
      finishExport,
      cancelExport,
      updateTransferProgress,
    }),
    [
      exportingProjectId,
      progress,
      phase,
      startExport,
      wasExportCancelled,
      finishExport,
      cancelExport,
      updateTransferProgress,
    ]
  );

  return <ExportStatusContext.Provider value={value}>{children}</ExportStatusContext.Provider>;
}

export function useExportStatus() {
  const context = useContext(ExportStatusContext);
  if (!context) {
    throw new Error('useExportStatus must be used within an ExportStatusProvider');
  }
  return context;
}
